import React, { useContext, useMemo } from 'react'
import moment from 'moment'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { max } from 'lib/array/arrayUtils'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import AtolProtectedTooltip from '../AtolProtectedTooltip'
import { rem } from 'polished'
import styled from 'styled-components'
import Caption from 'LuxKit/Typography/Caption'
import classnames from 'clsx'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'

const PackagePrices = styled(Caption)`
  display: flex;
  flex: 1;
`

const PackagePrice = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(20)};
`

interface Props {
  offer: App.Offer
  packageView: App.OfferPackageOptionView
  isLoggedIn: boolean
  checkIn?: string
  fetching?: boolean
  currency: string
  addAllFlights?: boolean
  buyNowBookLater?: boolean
  room: App.RoomOccupants
  defaultDepositAmountPercentage: number
  isFlightsSelected: boolean
  isFlightsEnabled: boolean
  flightPrice?: number
  flightOrigin?: App.Airport
  airports?: Array<App.Airport>
}

function MultipleRoomsBookingModalPriceDetails(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    fetching,
    currency,
    buyNowBookLater,
    room,
    defaultDepositAmountPercentage,
    isFlightsSelected,
    isFlightsEnabled,
    flightPrice = packageView.flightPrice,
    flightOrigin,
    airports,
  } = props

  const {
    package: pkg,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees = 0,
  } = packageView

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const showIncludedGuestsExceeded = useMemo(() => !!room && hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showMemberPrice = baseMemberPrice > 0
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = max([hotelDiscountPercent, bundledDiscountPercent])

  const totalPrice = hotelPrice + (propertyFees ?? 0)
  const totalMemberPrice = useMemo(() => {
    if (hotelMemberPrice > 0) {
      return hotelMemberPrice + (propertyFees ?? 0)
    }
    return 0
  }, [hotelMemberPrice, propertyFees])

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const hotelPriceOrHotelMemberPrice = showMemberPrice ? hotelMemberPrice : hotelPrice
  const shouldShowValue = hotelValue > hotelPriceOrHotelMemberPrice && pkg.shouldDisplayValue
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, hotelPriceOrHotelMemberPrice) && !buyNowBookLater
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const flightDestination = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
  }, [checkInMoment])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView, skip: !isFlightsSelected }),
  ], [offer, packageView, isFlightsSelected, currentRegionCode])

  return (
    <Group direction="vertical" gap={12}>
      <Group direction="vertical" gap={8}>
        {isDepositEnabled && <div>
          <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds} />
        </div>}
        <PackagePrices variant="medium" className={classnames({ show_discount: shouldShowDiscount || isDepositEnabled })}>
          {!offer.bundledWithFlightsOnly && !isFlightsSelected && (
            <PackagePrice data-testid="pricing">
              <PriceRowPriceCaption>{pluralizeToString('night', pkg.duration)} {!hasFlights && 'from'}</PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={totalPrice}
                memberPrice={totalMemberPrice}
                saleUnit="total"
                offerConfig={offer.luxPlus}
              />
              <PriceRowTaxesAndFees />
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={hotelValue}
                discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
              />}
              {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={pkg.roomPolicyDescription}
              />}
            </PackagePrice>
          )}

          {isFlightsEnabled && isFlightsSelected && (
            <PackagePrice data-testid="flights-pricing">
              {!hasFlights && flightOrigin?.code === flightDestination?.code && (
                <ChangeDepartureWrapper>
                  <PriceRowCaption>
                    No flights available from {flightOrigin?.name}. Please select another departing airport.
                  </PriceRowCaption>
                </ChangeDepartureWrapper>
              )}
              {!hasFlights && !flightsBundledNoCache && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture && (
                <>
                  <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                    {pluralizeToString('night', pkg.duration)} + flights from
                  </PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    size="S"
                    price={totalPrice}
                    saleUnit="total*"
                    memberPrice={totalMemberPrice}
                    offerConfig={offer.luxPlus}
                  />
                  <PriceRowTaxesAndFees />
                </>
              )}
              {!hasFlights && departureTooFarInFuture && (
                <ChangeDepartureWrapper>
                  <PriceRowCaption>
                    Flights can only be booked up to 11 months in advance.
                  </PriceRowCaption>
                </ChangeDepartureWrapper>
              )}
              {hasFlights && <>
                <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                  {pluralizeToString('night', pkg.duration)} + flights from
                </PriceRowPriceCaption>
                <LuxPlusPriceStack
                  size="S"
                  price={totalPrice + flightPrice}
                  memberPrice={totalMemberPrice > 0 ? totalMemberPrice + flightPrice : 0}
                  saleUnit="total*"
                  offerConfig={offer.luxPlus}
                />
                <PriceRowTaxesAndFees />
                {shouldShowValue && <PriceRowValueDiscountWithCaption
                  data-testid="value-box"
                  size="M"
                  originalValue={hotelValue + flightPrice}
                />}
                {showIncludedGuestsExceeded && (
                  <AdditionalGuestsPopup
                    complex={false}
                    description="+ Extra guest surcharge"
                    modalContent={pkg.roomPolicyDescription}
                  />
                )}
              </>}
              <AtolProtectedTooltip />
            </PackagePrice>
          )}
          <LoadingIndicator visible={fetching} floating opaque />
        </PackagePrices>
        <CSSBreakpoint min="tablet">
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="exact"
          />
        </CSSBreakpoint>
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  offer: state.offer,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  flightOrigin: state.airport,
  airports: appState.geo.airports,
}), MultipleRoomsBookingModalPriceDetails)
